/**
 * Businex API Service Layer
 * Connects to the Businex Backend at http://localhost:3000
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

/**
 * Generic fetch wrapper with JSON parsing and standardized error handling.
 */
async function request(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const errorMessage =
        data?.message ||
        data?.error ||
        `Request failed with status ${response.status} (${response.statusText})`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error(
        `Unable to reach Businex API server at ${API_BASE_URL}. Ensure the backend server is running.`
      );
    }
    throw error;
  }
}

/**
 * 1. System Health Check
 * GET /api/health
 */
export async function getSystemHealth() {
  return request('/api/health', {
    method: 'GET',
  });
}

/**
 * 2. Conversational RAG Chat
 * POST /api/chat
 * @param {Object} params
 * @param {string} params.message
 * @param {string} [params.sessionId]
 */
export async function sendChatMessage({ message, sessionId }) {
  const payload = { message };
  if (sessionId) {
    payload.sessionId = sessionId;
  }

  return request('/api/chat', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * 3. Get Conversation History by Session ID
 * GET /api/history/:sessionId
 * @param {string} sessionId
 */
export async function getConversationHistory(sessionId) {
  if (!sessionId) {
    throw new Error('sessionId is required to fetch conversation history.');
  }

  return request(`/api/history/${encodeURIComponent(sessionId)}`, {
    method: 'GET',
  });
}

/**
 * 4. Ingest Documents into ChromaDB Vector Store
 * POST /api/documents
 * @param {Object} params
 * @param {string[]} params.documents - Array of text snippets
 * @param {string[]} [params.ids] - Optional array of unique document IDs
 * @param {Object[]} [params.metadatas] - Optional array of metadata objects
 */
export async function ingestDocuments({ documents, ids, metadatas }) {
  if (!Array.isArray(documents) || documents.length === 0) {
    throw new Error('At least one document snippet is required for ingestion.');
  }

  const payload = { documents };
  if (ids && Array.isArray(ids)) payload.ids = ids;
  if (metadatas && Array.isArray(metadatas)) payload.metadatas = metadatas;

  return request('/api/documents', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * 5. Submit Contact Message
 * POST /api/contact
 * @param {Object} formData
 * @param {string} formData.firstName
 * @param {string} formData.lastName
 * @param {string} formData.email
 * @param {string} formData.companyName
 * @param {string} formData.message
 */
export async function submitContactForm(formData) {
  const { firstName, lastName, email, companyName, message } = formData;

  if (!firstName || !lastName || !email || !message) {
    throw new Error('First Name, Last Name, Email, and Message are required.');
  }

  return request('/api/contact', {
    method: 'POST',
    body: JSON.stringify({
      firstName,
      lastName,
      email,
      companyName: companyName || '',
      message,
    }),
  });
}

const api = {
  getSystemHealth,
  sendChatMessage,
  getConversationHistory,
  ingestDocuments,
  submitContactForm,
  API_BASE_URL,
};

export default api;

